export type BulkSceneAction = 'APPROVE' | 'DISMISS' | 'REVIEW';

export interface BulkSceneResult {
  action: BulkSceneAction;
  done: number;
  /** One line per row the server refused — named, never folded into a total. */
  refused: { label: string; reason: string }[];
}

const DONE: Record<BulkSceneAction, (n: number) => string> = {
  APPROVE: (n) => `${String(n)} requirement${n === 1 ? '' : 's'} confirmed`,
  DISMISS: (n) => `${String(n)} requirement${n === 1 ? '' : 's'} set aside`,
  REVIEW: (n) => `${String(n)} scene${n === 1 ? '' : 's'} marked reviewed`,
};

/**
 * WHAT TO DO WITH THE PICKED SCENES — the actions that already exist one at a
 * time, over many (owner review 2026-10-27, point 5).
 *
 * THE SAME VERBS, ROW BY ROW. Each requirement is confirmed or set aside with
 * its own row version, and each scene is marked reviewed on its own, exactly
 * as the scene card does it — so every guard a single press passes, a bulk
 * press passes too, and one row a colleague changed in the meantime is refused
 * and NAMED instead of failing the batch or being overwritten.
 *
 * ONLY THE UNDECIDED. "We need these" and "We don't need these" act on the
 * requirements on the picked scenes that nobody has decided yet. A decided one
 * is left as it is: a bulk press over forty scenes is not where an earlier
 * decision should be reversed without anyone looking at it.
 *
 * A REQUIREMENT IS SHARED BY ITS SCENES. Confirming one picked here confirms it
 * for every scene it covers, picked or not — the count says how many are
 * involved before the press, as the scene card's "shared with" line does.
 */
export function BulkSceneActions({
  pickedCount,
  undecidedCount,
  unreviewedCount,
  busy = false,
  result = null,
  onApprove,
  onDismiss,
  onMarkReviewed,
  onClear,
}: {
  pickedCount: number;
  /** Undecided requirements on the picked scenes. */
  undecidedCount: number;
  /** Picked scenes not yet marked reviewed. */
  unreviewedCount: number;
  busy?: boolean;
  result?: BulkSceneResult | null;
  onApprove: () => void;
  onDismiss: () => void;
  onMarkReviewed: () => void;
  onClear: () => void;
}) {
  return (
    <div
      data-testid="bulk-scene-actions"
      role="region"
      aria-label="Actions for the picked scenes"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 5,
        marginTop: 16,
        padding: '12px 14px',
        background: 'var(--color-bg)',
        border: '1px solid var(--color-accent)',
        boxShadow: '0 6px 18px rgba(16, 24, 40, 0.08)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 13.5 }} data-testid="bulk-scene-count">
          {`${String(pickedCount)} scene${pickedCount === 1 ? '' : 's'} picked`}
        </strong>
        <span className="mm" style={{ fontSize: 12.5 }}>
          {undecidedCount === 0
            ? 'No undecided requirement on them'
            : `${String(undecidedCount)} undecided requirement${undecidedCount === 1 ? '' : 's'} on them`}
        </span>
        <button
          type="button"
          className="ctl"
          data-testid="bulk-scene-clear"
          disabled={busy}
          onClick={onClear}
          style={{ marginLeft: 'auto' }}
        >
          Clear selection
        </button>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
        <button
          type="button"
          className="btn btn-primary"
          data-testid="bulk-scene-approve"
          disabled={busy || undecidedCount === 0}
          onClick={onApprove}
        >
          {`We need these locations (${String(undecidedCount)})`}
        </button>
        <button
          type="button"
          className="btn btn-ghost"
          data-testid="bulk-scene-dismiss"
          disabled={busy || undecidedCount === 0}
          onClick={onDismiss}
        >
          {`We don't need these (${String(undecidedCount)})`}
        </button>
        <button
          type="button"
          className="btn btn-ghost"
          data-testid="bulk-scene-review"
          disabled={busy || unreviewedCount === 0}
          onClick={onMarkReviewed}
        >
          {`Mark reviewed (${String(unreviewedCount)})`}
        </button>
      </div>

      {busy ? (
        <p className="mm" role="status" style={{ margin: '10px 0 0', fontSize: 12.5 }} data-testid="bulk-scene-busy">
          Working through them one by one…
        </p>
      ) : null}

      {result === null || busy ? null : (
        <div role="status" data-testid="bulk-scene-result" style={{ marginTop: 10, fontSize: 12.5 }}>
          <span style={{ color: 'var(--ok)', fontWeight: 600 }}>{DONE[result.action](result.done)}</span>
          {result.refused.length === 0 ? null : (
            <>
              <span style={{ color: 'var(--danger)', fontWeight: 600 }}>
                {` · ${String(result.refused.length)} refused`}
              </span>
              <ul style={{ margin: '6px 0 0', paddingLeft: 18, lineHeight: 1.6 }}>
                {result.refused.map((r, i) => (
                  <li key={`${r.label}-${String(i)}`} data-testid="bulk-scene-refused">
                    <strong>{r.label}</strong> <span className="mm">— {r.reason}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}
